// components/meta-comissao/MetaComissaoModal.tsx
import React, { useState, useEffect } from 'react';
import { MetaComissaoMensal, MetaProdutoComissao } from '../../types/entities';
import { metaComissaoService, metaProdutoComissaoService } from '../../services/metaComissaoService';
import { formatCurrency } from '../../utils/formatters';
import { useNotifier } from '../ui/Notifier';

interface Props {
  meta?: MetaComissaoMensal;
  isOpen: boolean;
  isEditing?: boolean;
  onClose: () => void;
  onSave: (meta: MetaComissaoMensal) => void;
}

interface ProdutoForm {
  id?: string;
  produto: string;
  valorObjetivoVenda: number;
  percentualParticipacao: number;
  percentualDisplay?: string;
  valorDisplay?: string;
}

export const MetaComissaoModal: React.FC<Props> = ({
  meta,
  isOpen,
  isEditing = false,
  onClose,
  onSave
}) => {
  // Form state
  const [formData, setFormData] = useState({
    mesAno: '',
    valorPremio: 0,
    valorComissao: 0,
    percentualBase: 0
  });

  const [produtos, setProdutos] = useState<ProdutoForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const notifier = useNotifier();
  const [metaSalva, setMetaSalva] = useState<MetaComissaoMensal | null>(null);
  const [displayPercentualBase, setDisplayPercentualBase] = useState<string>('');
  const [displayValorPremio, setDisplayValorPremio] = useState<string>('');
  const [displayValorComissao, setDisplayValorComissao] = useState<string>('');

  // Lista de produtos disponíveis
  const produtosDisponiveis = [
    'AUTO',
    'VIDA',
    'RESIDENCIAL', 
    'EMPRESARIAL',
    'ACIDENTES PESSOAIS',
    'VIAGEM',
    'RURAL',
    'TRANSPORTE',
    'RESPONSABILIDADE CIVIL',
    'GARANTIA ESTENDIDA',
    'PET',
    'DENTAL',
    'OUTROS'
  ].sort();

  // Load form data when meta prop changes
  useEffect(() => {
    if (meta) {
      setFormData({
        mesAno: meta.mesAno || '',
        valorPremio: meta.valorPremio || 0,
        valorComissao: meta.valorComissao || 0,
        percentualBase: meta.percentualBase ? meta.percentualBase * 100 : 0 // Convert to percentage for display
      });

      // set display for percentualBase and produtos
      setDisplayPercentualBase(meta.percentualBase ? String((meta.percentualBase * 100)).replace('.',',') + '%' : '');
      setDisplayValorPremio(meta.valorPremio ? String(formatCurrency(meta.valorPremio)) : '');
      setDisplayValorComissao(meta.valorComissao ? String(formatCurrency(meta.valorComissao)) : '');
      if (isEditing && meta.produtos) {
        setProdutos(meta.produtos.map(p => ({
            id: p.id,
            produto: p.produto,
            valorObjetivoVenda: p.valorObjetivoVenda || 0,
            percentualParticipacao: p.percentualParticipacao ? p.percentualParticipacao * 100 : 0,
            percentualDisplay: p.percentualParticipacao ? String((p.percentualParticipacao * 100)).replace('.',',') + '%' : '',
            valorDisplay: p.valorObjetivoVenda ? String(formatCurrency(p.valorObjetivoVenda)) : ''
          })));
      } else {
        setProdutos([]);
      }
    } else {
      // Reset form for new meta
      setFormData({
        mesAno: '',
        valorPremio: 0,
        valorComissao: 0,
        percentualBase: 0
      });
      setProdutos([]);
      setMetaSalva(null);
      setDisplayPercentualBase('');
      setDisplayValorPremio('');
      setDisplayValorComissao('');
    }
  }, [meta, isEditing]);

  // No catalog loading needed for empresa on this modal (empresa is optional)

  // Handlers
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helpers for percentage formatting/parsing
  const formatPercentageDisplay = (n?: number) => {
    if (n === undefined || n === null || Number.isNaN(n)) return '';
    const fixed = Number(n).toFixed(1).replace('.',',');
    return fixed.endsWith(',0') ? fixed.replace(',0','') + '%' : fixed + '%';
  };

  const parsePercentageInput = (s: string): number => {
    if (!s) return 0;
    const cleaned = s.replace('%','').replace(/\s+/g,'').replace(',', '.');
    const v = parseFloat(cleaned);
    return Number.isNaN(v) ? 0 : v;
  };

  // Currency parsing/formatting helpers
  const parseCurrencyInput = (s: string): number => {
    if (!s) return 0;
    // remove currency symbol, spaces; remove thousand separators (.) and replace decimal comma with dot
    let cleaned = s.replace('R$','').replace(/\s+/g,'').replace(/\./g,'').replace(',','.');
    cleaned = cleaned.replace(/[^(0-9\-.)]/g, '');
    const v = parseFloat(cleaned);
    return Number.isNaN(v) ? 0 : v;
  };

  const handleValorPremioChange = (value: string) => {
    setDisplayValorPremio(value);
    const parsed = parseCurrencyInput(value);
    setFormData(prev => ({ ...prev, valorPremio: parsed }));
  };

  const handleValorComissaoChange = (value: string) => {
    setDisplayValorComissao(value);
    const parsed = parseCurrencyInput(value);
    setFormData(prev => ({ ...prev, valorComissao: parsed }));
  };

  const handleCurrencyFocus = (value: string) => {
    return value ? value.replace('R$','').trim() : '';
  };

  const handleCurrencyBlurFormat = (n: number | undefined | null) => {
    return n ? formatCurrency(n) : '';
  };

  const handlePercentualBaseChange = (value: string) => {
    setDisplayPercentualBase(value);
    const parsed = parsePercentageInput(value);
    setFormData(prev => ({ ...prev, percentualBase: parsed }));
  };

  const handlePercentualBaseFocus = () => {
    setDisplayPercentualBase(prev => prev ? prev.toString().replace('%','') : '');
  };

  const handlePercentualBaseBlur = () => {
    const formatted = formatPercentageDisplay(formData.percentualBase);
    setDisplayPercentualBase(formatted);
  };

  const handleAddProduto = async () => {
    // Se não há meta salva ainda, salvar primeiro
    if (!metaSalva && !isEditing) {
      const saved = await salvarMetaSemProdutos();
      if (!saved) return;
    }

    setProdutos(prev => [
      ...prev,
      {
        produto: '',
        valorObjetivoVenda: 0,
        percentualParticipacao: 0,
        percentualDisplay: '',
        valorDisplay: ''
      }
    ]);
  };

  const handleRemoveProduto = (index: number) => {
    setProdutos(prev => prev.filter((_, i) => i !== index));
  };

  const handleProdutoChange = (index: number, field: string, value: any) => {
    setProdutos(prev => prev.map((produto, i) => {
      if (i === index) {
        const updated = { ...produto } as ProdutoForm;
        if (field === 'percentualParticipacao') {
          const parsed = typeof value === 'string' ? parsePercentageInput(value) : Number(value || 0);
          updated.percentualParticipacao = parsed;
          updated.percentualDisplay = typeof value === 'string' ? value : String(parsed).replace('.',',');
          const metaTotal = formData.valorPremio || formData.valorComissao || 0;
          if (metaTotal > 0 && parsed > 0) {
            updated.valorObjetivoVenda = (metaTotal * parsed) / 100;
          }
        } else {
          (updated as any)[field] = value;
        }
        return updated;
      }
      return produto;
    }));
  };

  // Calculations
  const getTotalPercentualParticipacao = () => {
    return produtos.reduce((sum, p) => sum + (p.percentualParticipacao || 0), 0);
  };

  const getTotalValorObjetivo = () => {
    return produtos.reduce((sum, p) => sum + (p.valorObjetivoVenda || 0), 0);
  };

  const calcularComissaoAutomatica = () => {
    if (formData.valorPremio && formData.percentualBase) {
      const comissao = (formData.valorPremio * formData.percentualBase) / 100;
      handleInputChange('valorComissao', comissao);
    }
  };

  // Salvar meta sem produtos (apenas dados básicos)
  const salvarMetaSemProdutos = async (): Promise<boolean> => {
    // Validar apenas dados básicos da meta
    const errors: string[] = [];
    
    if (!formData.mesAno.match(/^\d{2}\/\d{4}$/)) {
      errors.push('Formato do Mês/Ano deve ser MM/AAAA');
    }

    if (formData.valorPremio <= 0 && formData.valorComissao <= 0) {
      errors.push('Defina ao menos uma meta (prêmio ou comissão)');
    }

    if (errors.length > 0) {
      notifier.show({ message: 'Complete os dados básicos da meta. Verifique os erros e tente novamente.', severity: 'warning' });
      return false;
    }

    setSaving(true);
    try {
      const metaData = {
        mesAno: formData.mesAno,
        valorPremio: formData.valorPremio > 0 ? formData.valorPremio : undefined,
        valorComissao: formData.valorComissao > 0 ? formData.valorComissao : undefined,
        percentualBase: formData.percentualBase > 0 ? formData.percentualBase / 100 : undefined,
        produtos: [] // Criar meta sem produtos primeiro
      };

      const result = await metaComissaoService.createWithProdutos(metaData);
      
      if (result.data) {
        setMetaSalva(result.data);
        notifier.show({ message: 'Meta criada! Agora você pode adicionar produtos.', severity: 'success' });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
      notifier.show({ message: 'Erro ao salvar meta. Verifique os dados e tente novamente.', severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Validation
  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.mesAno.match(/^\d{2}\/\d{4}$/)) {
      errors.push('Formato do Mês/Ano deve ser MM/AAAA');
    }

    if (formData.valorPremio <= 0 && formData.valorComissao <= 0) {
      errors.push('Defina ao menos uma meta (prêmio ou comissão)');
    }

    // Só exigir produtos se já tiver meta salva ou estiver editando
    if (produtos.length === 0 && (metaSalva || isEditing)) {
      errors.push('Adicione ao menos um produto à meta');
    }

    produtos.forEach((produto, index) => {
      if (!produto.produto) {
        errors.push(`Produto ${index + 1}: Selecione o tipo de produto`);
      }
      if (produto.valorObjetivoVenda <= 0) {
        errors.push(`Produto ${index + 1}: Valor objetivo deve ser maior que zero`);
      }
      if (produto.percentualParticipacao <= 0) {
        errors.push(`Produto ${index + 1}: Percentual de participação deve ser maior que zero`);
      }
    });

    const totalParticipacao = getTotalPercentualParticipacao();
    if (Math.abs(totalParticipacao - 100) > 0.1) {
      errors.push(`Total de participação deve ser 100% (atual: ${totalParticipacao.toFixed(1)}%)`);
    }

    // Check for duplicate products
    const produtoNames = produtos.map(p => p.produto).filter(Boolean);
    const uniqueNames = new Set(produtoNames);
    if (produtoNames.length !== uniqueNames.size) {
      errors.push('Não é possível ter produtos duplicados na mesma meta');
    }

    return errors;
  };

  const handleSave = async () => {
    // Se não há meta salva e não está editando, criar a meta primeiro
    if (!metaSalva && !isEditing) {
      const saved = await salvarMetaSemProdutos();
      if (!saved) return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      notifier.show({ message: 'Erros encontrados nos dados da meta. Verifique os campos.', severity: 'warning' });
      return;
    }

    setSaving(true);
    try {
      let metaFinal: MetaComissaoMensal;

      // Atualizar dados básicos da meta
      const metaData = {
        mesAno: formData.mesAno,
        valorPremio: formData.valorPremio > 0 ? formData.valorPremio : undefined,
        valorComissao: formData.valorComissao > 0 ? formData.valorComissao : undefined,
        percentualBase: formData.percentualBase > 0 ? formData.percentualBase / 100 : undefined
      };

      if (isEditing && meta) {
        const result = await metaComissaoService.update(meta.id, metaData);
        metaFinal = result.data;
      } else if (metaSalva) {
        const result = await metaComissaoService.update(metaSalva.id, metaData);
        metaFinal = result.data;
      } else {
        const result = await metaComissaoService.create(metaData);
        metaFinal = result.data;
      }

      // Salvar produtos individualmente usando o endpoint correto
      for (const produto of produtos) {
        if (produto.id) {
          // Atualizar produto existente
          await metaProdutoComissaoService.updateProduto(produto.id, {
            produto: produto.produto,
            valorObjetivoVenda: produto.valorObjetivoVenda,
            percentualParticipacao: produto.percentualParticipacao / 100
          });
        } else {
          // Criar novo produto
          await metaProdutoComissaoService.addProdutoToMeta({
            produto: produto.produto,
            percentualParticipacao: produto.percentualParticipacao / 100,
            valorObjetivoVenda: produto.valorObjetivoVenda,
            metaMensalId: metaFinal.id
          });
        }
      }

      onSave(metaFinal);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
      notifier.show({ message: 'Erro ao salvar meta. Verifique os dados e tente novamente.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b" style={{ backgroundColor: '#7A61B2', color: 'white' }}>
          <h2 className="text-xl font-bold text-white">
            {isEditing ? 'Editar Meta de Comissão' : 'Nova Meta de Comissão'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Dados básicos da meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mês/Ano *
              </label>
              <input
                type="text"
                value={formData.mesAno}
                onChange={(e) => handleInputChange('mesAno', e.target.value)}
                placeholder="MM/AAAA (ex: 01/2025)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Empresa removed from meta form - not required */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                % Base Comissão
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={displayPercentualBase}
                  onChange={(e) => handlePercentualBaseChange(e.target.value)}
                  onFocus={handlePercentualBaseFocus}
                  onBlur={handlePercentualBaseBlur}
                  placeholder="12,5"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={calcularComissaoAutomatica}
                  className="px-3 py-2 text-white rounded-r-md text-sm"
                  style={{ backgroundColor: '#7A61B2' }}
                  title="Calcular comissão automática"
                >
                  %
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Prêmio (R$)
              </label>
              <input
                type="text"
                value={displayValorPremio}
                onChange={(e) => handleValorPremioChange(e.target.value)}
                onFocus={(e) => setDisplayValorPremio(handleCurrencyFocus(displayValorPremio))}
                onBlur={() => setDisplayValorPremio(handleCurrencyBlurFormat(formData.valorPremio))}
                placeholder="0,00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="text-sm text-gray-600 mt-1">{formatCurrency(formData.valorPremio)}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Comissão (R$)
              </label>
              <input
                type="text"
                value={displayValorComissao}
                onChange={(e) => handleValorComissaoChange(e.target.value)}
                onFocus={(e) => setDisplayValorComissao(handleCurrencyFocus(displayValorComissao))}
                onBlur={() => setDisplayValorComissao(handleCurrencyBlurFormat(formData.valorComissao))}
                placeholder="0,00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="text-sm text-gray-600 mt-1">{formatCurrency(formData.valorComissao)}</div>
            </div>
          </div>

          {/* Status da meta */}
          {!isEditing && metaSalva && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
              <div className="flex">
                <div className="text-green-600 mr-2">✅</div>
                <div>
                  <p className="text-sm text-green-800 font-medium">Meta salva com sucesso!</p>
                  <p className="text-sm text-green-700">Agora você pode adicionar produtos à meta.</p>
                </div>
              </div>
            </div>
          )}

          {/* Produtos da meta */}
          <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Produtos da Meta
              </h3>
              <button
                type="button"
                onClick={handleAddProduto}
                disabled={saving}
                className="px-4 py-2 text-white rounded-md text-sm transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#7A61B2' }}
              >
                {saving ? 'Salvando...' : (!metaSalva && !isEditing ? '📝 Salvar Meta e Adicionar Produto' : '+ Adicionar Produto')}
              </button>
            </div>

            {produtos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum produto adicionado. Clique em "Adicionar Produto" para começar.
              </div>
            ) : (
              <div className="space-y-4">
                {produtos.map((produto, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Produto *
                        </label>
                        <input
                          type="text"
                          list="produtos-datalist"
                          value={produto.produto}
                          onChange={(e) => handleProdutoChange(index, 'produto', e.target.value)}
                          placeholder="Digite o produto (ex: AUTO)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <datalist id="produtos-datalist">
                          {produtosDisponiveis.map((nome) => (
                            <option key={nome} value={nome} />
                          ))}
                        </datalist>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          % Participação *
                        </label>
                        <input
                          type="text"
                          value={produto.percentualDisplay ?? (produto.percentualParticipacao ? String(produto.percentualParticipacao).replace('.',',') : '')}
                          onChange={(e) => handleProdutoChange(index, 'percentualParticipacao', e.target.value)}
                          placeholder="12,5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Valor Objetivo (R$) *
                        </label>
                        <input
                          type="text"
                          value={produto.valorDisplay ?? (produto.valorObjetivoVenda ? String(formatCurrency(produto.valorObjetivoVenda)) : '')}
                          onChange={(e) => {
                            const v = e.target.value;
                            // update display and numeric
                            setProdutos(prev => prev.map((p, i) => i === index ? ({ ...p, valorDisplay: v, valorObjetivoVenda: parseCurrencyInput(v) }) : p));
                          }}
                          onFocus={(e) => setProdutos(prev => prev.map((p, i) => i === index ? ({ ...p, valorDisplay: (p.valorDisplay || '').replace('R$','').trim() }) : p))}
                          onBlur={(e) => setProdutos(prev => prev.map((p, i) => i === index ? ({ ...p, valorDisplay: formatCurrency(p.valorObjetivoVenda) }) : p))}
                          placeholder="0,00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveProduto(index)}
                          className="w-full px-3 py-2 text-white rounded-md hover:bg-red-500 text-sm"
                          style={{ backgroundColor: '#F87171' }}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Totalizadores */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Total Valor Objetivo:</span>
                      <span className="ml-2 text-purple-600 font-bold">
                        {formatCurrency(getTotalValorObjetivo())}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Total % Participação:</span>
                      <span className={`ml-2 font-bold ${
                        Math.abs(getTotalPercentualParticipacao() - 100) <= 0.1 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {getTotalPercentualParticipacao().toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Produtos:</span>
                      <span className="ml-2 text-gray-600">
                        {produtos.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-white rounded-md disabled:opacity-50"
            style={{ backgroundColor: '#7A61B2' }}
          >
            {saving ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar Meta')}
          </button>
        </div>
      </div>
    </div>
  );
};